'use client';

import type { BioContext } from '@ps/functions/bio/bioContext';
import { useBehaviorSubject } from '@ps/functions/hooks/useBehaviorSubject';
import { Loader2 } from 'lucide-react';

import { ChannelDetailPane } from '../../community-channels/components/channel-detail/ChannelDetailPane';
import { useCommunityContext } from '../../community-channels/contexts/community/useCommunityContext';
import { canViewChannel } from '../../community-channels/functions/access/canViewChannel';
import { memberHasChannelSubscriptionAccess } from '../../community-channels/functions/access/memberHasChannelSubscriptionAccess';
import { useMyChannelMembership } from '../../community-channels/hooks/useMyChannelMembership';
import { ChannelVisibility } from '../../community-channels/types/ChannelVisibility';
import { useBioUserAccess } from '../contexts/user-access/useBioUserAccess';
import { ChannelJoinGate } from './ChannelJoinGate';
import { useAutoJoinChannelAfterSubscription } from './useAutoJoinChannelAfterSubscription';

interface Props {
  slug: string;
  bioContext: BioContext;
}

/**
 * Visitor channel detail. Public channels read freely. A private channel needs
 * membership — the server returns an empty feed until the viewer joins — so a
 * private channel the viewer hasn't joined renders the {@link ChannelJoinGate}
 * (join, or subscribe-then-join). Once membership lands (live), the shared
 * {@link ChannelDetailPane} swaps in with the real message history.
 */
export function VisitorChannelDetail({ slug, bioContext }: Props) {
  const { channelsState$, isOwner } = useCommunityContext();
  const { extendedSubscriptionLevel, loading } = useBioUserAccess();
  const [channelsState] = useBehaviorSubject(channelsState$);
  const channel = channelsState.channels.find(item => item.slug === slug);
  const membership = useMyChannelMembership(channel?.id);

  // Subscribing from the join gate should drop the viewer straight into the
  // channel — auto-join once the subscription checkout is dismissed and the new
  // level meets the bar (PopshopRN's post-subscribe continuation).
  useAutoJoinChannelAfterSubscription(channel, membership);

  // Channel list still resolving or slug not found — let the pane render its own
  // loading / not-found state.
  if (!channel) {
    return <ChannelDetailPane slug={slug} />;
  }

  // Public channels are readable without a membership.
  if (channel.visibility === ChannelVisibility.Public) {
    return <ChannelDetailPane slug={slug} />;
  }

  // Private: wait until membership + subscription standing resolve.
  if (membership === undefined || loading) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center">
        <Loader2
          className="text-checkout-neutral-500 h-6 w-6 animate-spin"
          aria-hidden
        />
      </div>
    );
  }

  // Reading a private channel requires an ACTIVE membership AND a satisfying
  // subscription level (the owner always passes). Membership alone isn't enough:
  // the backend gates the private feed on the current subscription level, so a
  // member who dropped below the bar would otherwise land in an empty channel
  // instead of the join/subscribe blocker.
  if (
    canViewChannel({
      visibility: channel.visibility,
      minimumSubscriptionLevel: channel.minimumSubscriptionLevel,
      isOwner,
      member: membership,
      viewerLevel: extendedSubscriptionLevel,
    })
  ) {
    return <ChannelDetailPane slug={slug} />;
  }

  // Blocked from reading — the gate offers "Join Channel Now!" when the viewer
  // already meets the level (or is grandfathered), else "Subscribe & Join".
  const hasSubscriptionAccess = memberHasChannelSubscriptionAccess({
    viewerLevel: extendedSubscriptionLevel,
    channelMinimumSubscriptionLevel: channel.minimumSubscriptionLevel,
    member: membership,
  });

  return (
    <ChannelJoinGate
      channel={channel}
      bioContext={bioContext}
      hasSubscriptionAccess={hasSubscriptionAccess}
    />
  );
}
